import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { Monitor, Smartphone, LogOut, Shield, MapPin, Clock, Trash2, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deviceIcon = (label = "") => {
  const l = label.toLowerCase();
  if (l.includes("iphone") || l.includes("ios") || l.includes("android"))
    return <Smartphone className="w-4 h-4" />;
  return <Monitor className="w-4 h-4" />;
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch {
    return iso;
  }
};

const SessionRow = ({ session, onRevoke, showEmail = false, revoking, currentCountry }) => {
  const geoText = [session.geo_city, session.geo_country].filter(Boolean).join(", ");
  const countryMismatch =
    currentCountry &&
    session.geo_country_code &&
    session.geo_country_code !== currentCountry &&
    !session.is_current;

  return (
  <div
    className={`flex items-center gap-4 px-4 py-3 rounded-lg border ${
      session.is_current
        ? "border-[#EC008C]/60 bg-[#EC008C]/[0.05]"
        : countryMismatch
        ? "border-red-500/60 bg-red-500/[0.06]"
        : "border-[#1A2030] bg-[#0A0E18]"
    }`}
    data-testid={`session-row-${session.session_id}`}
  >
    <div className="text-[#EC008C]">{deviceIcon(session.device_label)}</div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-white font-semibold truncate">
          {session.device_label || session.user_agent?.slice(0, 40) || "Unknown device"}
        </span>
        {session.is_current && (
          <span className="text-xs uppercase tracking-wide bg-[#EC008C] text-black px-2 py-0.5 rounded font-bold">
            Current
          </span>
        )}
        {session.is_admin && (
          <span className="text-xs uppercase tracking-wide bg-purple-500/20 border border-purple-400 text-purple-300 px-2 py-0.5 rounded">
            Admin
          </span>
        )}
        {countryMismatch && (
          <span
            className="text-xs uppercase tracking-wide bg-red-500/25 border border-red-400 text-red-200 px-2 py-0.5 rounded font-bold"
            data-testid={`country-mismatch-${session.session_id}`}
          >
            Foreign country
          </span>
        )}
      </div>
      <div className="text-xs text-[#8A92A0] flex items-center gap-3 mt-1 flex-wrap">
        {showEmail && session.email && (
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" /> {session.email}
          </span>
        )}
        <span
          className={`flex items-center gap-1 ${countryMismatch ? "text-red-300 font-semibold" : ""}`}
          data-testid={`session-geo-${session.session_id}`}
        >
          <MapPin className="w-3 h-3" />
          {geoText || "Unknown location"}
          {session.ip ? <span className="opacity-60 ml-1">· {session.ip}</span> : null}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" /> Active {timeAgo(session.last_seen_at || session.created_at)}
        </span>
      </div>
    </div>
    {!session.is_current && (
      <Button
        onClick={() => onRevoke(session.session_id)}
        disabled={revoking === session.session_id}
        variant="destructive"
        size="sm"
        className="shrink-0"
        data-testid={`revoke-btn-${session.session_id}`}
      >
        <Trash2 className="w-3 h-3 mr-1.5" />
        {revoking === session.session_id ? "Revoking..." : "Revoke"}
      </Button>
    )}
  </div>
  );
};

const SessionsAndSecurity = () => {
  const { token, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [killing, setKilling] = useState(false);
  const isAdmin = !!user?.is_admin;

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/me/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(res.data.rows || []);
      if (isAdmin) {
        const ar = await axios.get(`${API}/admin/sessions?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdminSessions(ar.data.rows || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const revokeMine = async (session_id) => {
    setRevoking(session_id);
    try {
      await axios.post(
        `${API}/me/sessions/revoke`,
        { session_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Session revoked");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally {
      setRevoking(null);
    }
  };

  const revokeAdmin = async (session_id) => {
    setRevoking(session_id);
    try {
      await axios.post(
        `${API}/admin/sessions/revoke`,
        { session_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Session revoked platform-wide");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally {
      setRevoking(null);
    }
  };

  const killSwitch = async () => {
    if (!window.confirm("Sign out every other device on this account? Your current session will stay signed in.")) return;
    setKilling(true);
    try {
      const res = await axios.post(
        `${API}/me/sessions/revoke-others`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Kill switch fired — ${res.data.revoked_count} sessions revoked`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kill switch failed");
    } finally {
      setKilling(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="security-sessions-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-[#EC008C]" />
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white">Security &amp; Sessions</h1>
      </div>
      <p className="text-[#8A92A0] mb-8 text-sm">
        Every device signed into your StardomFans account. Revoke anything you don&apos;t recognise — instantly.
      </p>

      {(() => {
        const currentCountry =
          sessions.find((s) => s.is_current)?.geo_country_code || "";
        const foreignCount = sessions.filter(
          (s) => !s.is_current && s.geo_country_code && s.geo_country_code !== currentCountry
        ).length;
        if (!currentCountry || foreignCount === 0) return null;
        return (
          <div
            className="mb-6 rounded-lg border border-red-500/50 bg-red-500/[0.06] px-4 py-3 text-sm text-red-200 flex items-start gap-2"
            data-testid="foreign-session-alert"
          >
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
            <span>
              <strong className="text-red-100">Heads up:</strong> {foreignCount} session
              {foreignCount > 1 ? "s are" : " is"} signed in from a country different than your
              current one. Revoke anything you don&apos;t recognise.
            </span>
          </div>
        );
      })()}

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Unbounded'] text-xl font-semibold text-white">Your active sessions</h2>
          <Button
            onClick={killSwitch}
            disabled={killing || loading || sessions.filter((s) => !s.is_current).length === 0}
            variant="destructive"
            size="sm"
            data-testid="kill-switch-btn"
          >
            <LogOut className="w-3 h-3 mr-1.5" />
            {killing ? "Killing..." : "Kill switch (sign out everyone else)"}
          </Button>
        </div>
        {loading ? (
          <div className="text-[#8A92A0]">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="text-[#8A92A0]">No active sessions.</div>
        ) : (
          <div className="space-y-2" data-testid="my-sessions-list">
            {sessions.map((s) => (
              <SessionRow
                key={s.session_id}
                session={s}
                onRevoke={revokeMine}
                revoking={revoking}
                currentCountry={sessions.find((x) => x.is_current)?.geo_country_code || ""}
              />
            ))}
          </div>
        )}
      </section>

      {isAdmin && (
        <section className="border-t border-[#1A2030] pt-8">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-['Unbounded'] text-xl font-semibold text-white">Platform-wide overview</h2>
            <span className="text-xs uppercase tracking-wide bg-purple-500/20 border border-purple-400 text-purple-300 px-2 py-0.5 rounded">
              Admin only
            </span>
          </div>
          <p className="text-[#8A92A0] mb-4 text-sm">
            Every session across StardomFans. Revoke any device with one click. Total: <strong className="text-white">{adminSessions.length}</strong>
          </p>
          {adminSessions.length === 0 ? (
            <div className="text-[#8A92A0]">No sessions active.</div>
          ) : (
            <div className="space-y-2" data-testid="admin-sessions-list">
              {adminSessions.map((s) => (
                <SessionRow
                  key={s.session_id}
                  session={s}
                  onRevoke={revokeAdmin}
                  showEmail
                  revoking={revoking}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default SessionsAndSecurity;
